import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import icons from "../../../assets/images/constantIcons";
import apiService, { handleError } from "../../Common/ApiCommon/ApiService";
import toast from "react-hot-toast";
import Select from "react-select";

const EditRoleForm = ({ onClose, editPopup, fetchRoles, roleId }) => {
    const [roleData, setRoleData] = useState(null);
    const [modules, setModules] = useState([]);

    // Fetch Role Details
    useEffect(() => {
        const fetchRoleDetails = async () => {
            try {
                const response = await apiService.get(`roles/${roleId}`);
                setRoleData(response.role);
            } catch (errors) {
                handleError(errors);
            }
        };

        if (roleId) {
            fetchRoleDetails();
        }
    }, [roleId]);

    // Fetch Available Modules
    useEffect(() => {
        const fetchModules = async () => {
            try {
                const response = await apiService.get("roles/module_names");
                setModules(response.modules || []);
            } catch (error) {
                console.error("Failed to fetch module names:", error);
            }
        };

        fetchModules();
    }, []);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            name: roleData?.name || "",
            module_access: roleData?.module_access || [],
        },
        validationSchema: Yup.object({
            name: Yup.string()
                .min(3, "Role name must be at least 3 characters long")
                .max(30, "Role name cannot exceed 30 characters")
                .matches(/^[A-Za-z\s]+$/, "Role name cannot contain numbers or special characters")
                .required("Role name is required"),
            module_access: Yup.array().min(1, "Select at least one module"),
        }),
        onSubmit: async (values) => {
            try {
                const response = await apiService.put(`roles/${roleId}`, values);
                toast.success(response.message, { duration: 2000 });
                onClose();
                fetchRoles(); // Refresh role list after update
            } catch (errors) {
                handleError(errors);
            }
        },
    });

    return (
        editPopup && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-4 s:p-6 w-[310px] s:w-[350px] md:w-[460px] shadow-lg relative">
                    {/* Close Button */}
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-lg font-semibold">Edit Role</h2>
                        <div
                            className="absolute top-7 right-3 bg-green50 rounded-full p-2 cursor-pointer w-[32px] h-[32px]"
                            onClick={onClose}
                        >
                            <icons.IoClose className="text-green700" />
                        </div>
                    </div>

                    {/* Form */}
                    <form onSubmit={formik.handleSubmit} className="space-y-4 text-start h-auto overflow-y-auto vertical">
                        {/* Role Name */}
                        <div className="text-G300">
                            <label className="block text-sm mb-2">Role Name</label>
                            <input
                                type="text"
                                name="name"
                                value={formik.values.name}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                className="inputtext"
                                placeholder="Role Name"
                            />
                            {formik.errors.name && formik.touched.name && (
                                <div className="text-red text-sm">{formik.errors.name}</div>
                            )}
                        </div>

                        {/* Multi-Select Dropdown with Checkboxes */}
                        <div className="text-G300">
                            <label className="block font-normal text-sm mb-2">Module Access</label>
                            <Select
                                options={modules.map((module) => ({
                                    value: module.toLowerCase(),
                                    label: module.charAt(0).toUpperCase() + module.slice(1),
                                }))}
                                isMulti
                                name="module_access"
                                className="basic-multi-select"
                                classNamePrefix="select"
                                value={formik.values.module_access.map((module) => ({
                                    value: module.toLowerCase(),
                                    label: module.charAt(0).toUpperCase() + module.slice(1),
                                }))}
                                onChange={(selectedOptions) => {
                                    formik.setFieldValue(
                                        "module_access",
                                        selectedOptions.map((option) => option.value)
                                    );
                                }}
                                styles={{
                                    control: (baseStyles) => ({
                                        ...baseStyles,
                                        boxShadow: "none",
                                        borderColor: "#EDEDED",
                                    }),
                                }}
                            />

                            {formik.errors.module_access && formik.touched.module_access && (
                                <div className="text-red text-sm">{formik.errors.module_access}</div>
                            )}
                        </div>

                        {/* Buttons */}
                        <div className="flex justify-end space-x-4 pt-6">
                            <button
                                type="button"
                                onClick={() => {
                                    formik.resetForm();
                                    onClose();
                                }}
                                className="w-[75px] sm:w-[93px] text-sm py-2 text-center border border-green text-green rounded-lg"
                            >
                                Close
                            </button>
                            <button
                                type="submit"
                                className="w-[75px] sm:w-[93px] text-sm text-center py-2 bg-green text-white rounded-lg"
                            >
                                Update
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        )
    );
};

export default EditRoleForm;
